import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle,
  User,
  Calendar
} from 'lucide-react';
import { Feedback, FeedbackComment } from '../types';
import { Button } from './ui';
import { cn } from '../utils/cn';
import { formatDate } from '../utils/format';
import { useUser } from '../contexts/UserContext';
import { subscribeToFeedbackComments } from '../firebase/firestore';

interface FeedbackCardProps {
  feedback: Feedback;
  onEdit: (feedback: Feedback) => void;
  onDelete: (feedbackId: string) => void;
  onAddComment: (feedbackId: string, comment: string) => void;
  onDeleteComment: (feedbackId: string, commentId: string) => void;
  onUpdateStatus: (feedbackId: string, status: Feedback['status']) => void;
  onUpdatePriority: (feedbackId: string, priority: Feedback['priority']) => void;
  onMarkAsRead: (feedbackId: string) => void;
}

const priorityConfig = {
  low: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  medium: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  high: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  critical: { color: 'bg-red-100 text-red-800', icon: XCircle },
};

const statusConfig = {
  open: { color: 'bg-blue-100 text-blue-800', label: 'Open' },
  'in-progress': { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
  resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
  closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' },
};

const categoryConfig = {
  'bug-report': { color: 'bg-red-100 text-red-800', label: 'Bug Report' },
  'feature-request': { color: 'bg-blue-100 text-blue-800', label: 'Feature Request' },
  'ui-ux-issue': { color: 'bg-purple-100 text-purple-800', label: 'UI/UX Issue' },
  'general-feedback': { color: 'bg-gray-100 text-gray-800', label: 'General Feedback' },
};

export function FeedbackCard({
  feedback,
  onEdit,
  onDelete,
  onAddComment,
  onDeleteComment,
  onUpdateStatus,
  onUpdatePriority,
  onMarkAsRead,
}: FeedbackCardProps) {
  const { user, isAdmin, isManagement } = useUser();
  const [showComments, setShowComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [comments, setComments] = useState<FeedbackComment[]>(feedback.comments || []);

  // Subscribe to comment updates for this feedback
  useEffect(() => {
    const unsubscribe = subscribeToFeedbackComments(feedback.id, (updatedComments) => {
      setComments(updatedComments);
    });

    return () => unsubscribe();
  }, [feedback.id]);

  const canEdit = isAdmin || isManagement || feedback.submittedBy === user?.id;
  const canDelete = isAdmin || feedback.submittedBy === user?.id;
  const canUpdateStatus = isAdmin || isManagement;
  
  const canDeleteComment = (comment: FeedbackComment) => {
    return isAdmin || isManagement || comment.author === user?.id;
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setIsSubmittingComment(true);
    try {
      await onAddComment(feedback.id, newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleStatusChange = (newStatus: Feedback['status']) => {
    onUpdateStatus(feedback.id, newStatus);
  };

  const handlePriorityChange = (newPriority: Feedback['priority']) => {
    onUpdatePriority(feedback.id, newPriority);
  };

  const PriorityIcon = priorityConfig[feedback.priority].icon;

  return (
    <div className="bg-white/30 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-white/20">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{feedback.title}</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                categoryConfig[feedback.category].color
              )}>
                {categoryConfig[feedback.category].label}
              </span>
              <span className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                priorityConfig[feedback.priority].color
              )}>
                <PriorityIcon className="w-3 h-3 inline mr-1" />
                {feedback.priority.charAt(0).toUpperCase() + feedback.priority.slice(1)}
              </span>
              <span className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                statusConfig[feedback.status].color
              )}>
                {statusConfig[feedback.status].label}
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(feedback)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(feedback.id)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-700 text-sm leading-relaxed mb-4">{feedback.description}</p>

        {/* Meta Information */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span>{feedback.submittedByName}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(feedback.createdAt)}</span>
            </div>
          </div>
          
          {/* Resolution Info */}
          {feedback.resolvedAt && (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-3 h-3" />
              <span>Resolved by {feedback.resolvedByName}</span>
            </div>
          )}
        </div>
      </div>

              {/* Resolution Note */}
        {feedback.resolutionNote && (
          <div className="px-6 py-4 bg-emerald-50/50 backdrop-blur-sm border-b border-white/20">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800 mb-1">Resolution Note</p>
                <p className="text-sm text-emerald-700">{feedback.resolutionNote}</p>
              </div>
            </div>
          </div>
        )}

        {/* Status & Priority Controls (Admin/Management only) */}
        {canUpdateStatus && (
          <div className="px-6 py-3 bg-white/20 backdrop-blur-sm border-b border-white/20">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Status:</span>
              <select
                value={feedback.status}
                onChange={(e) => handleStatusChange(e.target.value as Feedback['status'])}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
              >
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Priority:</span>
              <select
                value={feedback.priority}
                onChange={(e) => handlePriorityChange(e.target.value as Feedback['priority'])}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>
      )}

              {/* Comments Section */}
        <div className="px-6 py-4 bg-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <MessageSquare className="w-4 h-4" />
            <span>{comments.length} Comments</span>
          </button>
          
          {/* Mark as read for admins */}
          {isAdmin && !feedback.isReadByAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMarkAsRead(feedback.id)}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Mark as Read
            </Button>
          )}
        </div>

        {showComments && (
          <div className="space-y-3">
            {/* Comments List */}
            {comments.map((comment) => (
              <div key={comment.id} className="bg-white/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{comment.authorName}</span>
                    <span className="text-xs text-gray-500">{formatDate(comment.timestamp)}</span>
                  </div>
                  {canDeleteComment(comment) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteComment(feedback.id, comment.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-700">{comment.content}</p>
              </div>
            ))}

            {/* Add Comment */}
            <div className="border-t border-white/20 pt-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 text-sm bg-white/50 backdrop-blur-xl border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 font-text"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isSubmittingComment}
                  size="sm"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
                >
                  {isSubmittingComment ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
